import { Button, Divider, lighten, Stack, Typography } from "@mui/material";
import { useIsMobile } from "src/hooks/useIsMobile";
import { useCallback, useMemo, useState } from "react";
import { usePrices } from "src/context/PricesProvider";
import { Coupon, ProductPrice } from "src/api/types";
import { useProduct } from "src/context/ProductProvider";
import { useTranslation } from "react-i18next";
import { theme } from "src/assets/theme";
import { handleFocusIntoView } from "src/utils/focusIntoView";
import TermsOfConditions from "../TermsOfConditions";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ReviewsCarousel from "../ReviewsCarousel";
import ApplyCoupon from "../ApplyCoupon";
import { useGetPatientCouponsQuery } from "src/api/services/patientCouponsApi";
import PaymentProcessor from "../PaymentProcessor";
import CouponView from "../CouponView";
import { useSelectedProduct } from "src/hooks/useSelectedProduct";
import ProductsDisplay from "src/components/ProductsDisplay";
import Header from "../Headers/Header";
import Product from "../Product";
import Subheader from "../Headers/Subheader";
import InformationArray from "../InformationArray";

const DiabetesPaymentInformation = () => {
  const isMobile = useIsMobile();
  const weightLossProducts = usePrices();
  const [selectedProduct, setSelectedProduct] = useSelectedProduct();
  const productSlug = useProduct((p) => p.slug);
  const page = "payment-information";
  const { t } = useTranslation();
  const isGlp1 = useProduct((product) => product.is_glp1);
  const [coupon, setCoupon] = useState<Coupon | null>(null);
  const { data: patientCoupons = [] } = useGetPatientCouponsQuery({
    product_slug: productSlug,
    redeemed: false,
  });

  const onboardingProducts = useMemo(() => {
    return weightLossProducts;
  }, [weightLossProducts]);

  const handleProductChange = useCallback(
    (product: ProductPrice) => {
      setSelectedProduct(product);
    },
    [setSelectedProduct],
  );

  const isQuarterly = useMemo(
    () => selectedProduct?.number_of_months === 3,
    [selectedProduct?.number_of_months],
  );

  const coupons = useMemo(() => {
    return patientCoupons.concat(coupon ? [coupon] : []);
  }, [coupon, patientCoupons]);

  const defaultProductPrice = useMemo(() => {
    if (selectedProduct == null) {
      return 0;
    }

    const additionalDiscount = coupons.reduce((acc, curr) => {
      return acc + curr.amount;
    }, 0);

    return Math.max(
      selectedProduct?.amount - additionalDiscount,
      selectedProduct.amount_limit ?? 0,
    );
  }, [coupons, selectedProduct]);

  const discountedPrice = useMemo(() => {
    if (selectedProduct == null) return "0";
    const discount = selectedProduct?.coupon?.amount ?? 0;

    return (selectedProduct.amount - discount).toLocaleString();
  }, [selectedProduct]);

  const firstMonthSaving = useMemo(() => {
    const defaultPrice = Number(defaultProductPrice);
    const discountPrice = Number(discountedPrice.replace(",", ""));

    return defaultPrice - discountPrice;
  }, [defaultProductPrice, discountedPrice]);

  const initialDiscount = useMemo(() => {
    if (selectedProduct == null || selectedProduct.competitor_price == null)
      return 0;
    const defaultPrice = selectedProduct.amount;

    return Math.abs(selectedProduct.competitor_price - defaultPrice);
  }, [selectedProduct]);

  const totalSavings = useMemo(() => {
    const additionalDiscount = coupons.reduce((acc, curr) => {
      return acc + curr.amount;
    }, 0);

    return Math.abs(initialDiscount + additionalDiscount);
  }, [coupons, initialDiscount]);

  return (
    <Stack gap={3}>
      <Stack gap={2}>
        <Stack gap={3}>
          <Header />
          <Product isQuarterly={isQuarterly} showFsaHsa={true} />
        </Stack>
        <Stack gap={2} onClick={() => handleFocusIntoView("payment-input")}>
          <Stack>
            <Typography variant={isMobile ? "body1" : "h6"} fontWeight={600}>
              <Subheader />
            </Typography>
          </Stack>
          <InformationArray />
        </Stack>
      </Stack>
      <Stack gap={3}>
        <ProductsDisplay
          selectedProduct={selectedProduct}
          page={page}
          handleProductChange={handleProductChange}
          products={onboardingProducts}
        />
        <ApplyCoupon setCoupon={setCoupon} coupon={null} />
        <Stack gap={4}>
          <Stack gap={2}>
            {initialDiscount === 0 ? null : (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  component={Stack}
                  direction="row"
                  alignItems="center"
                  gap={1}
                  fontSize={"18px"}
                  color="success"
                >
                  {t(`${page}.payNowDiscount`)} <LocalOfferIcon />
                </Typography>
                <Typography fontSize={"18px"} color="success">
                  -${initialDiscount}
                </Typography>
              </Stack>
            )}
            {coupons.map((c) => (
              <CouponView key={c.name} coupon={c} />
            ))}
            {firstMonthSaving > 0 ? (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  component={Stack}
                  direction="row"
                  alignItems="center"
                  gap={1}
                  fontSize={"15px"}
                  color="success"
                >
                  {t(`${page}.firstMonthDiscount`)} <LocalOfferIcon />
                </Typography>
                <Typography fontSize={"15px"} color="success">
                  -${firstMonthSaving}
                </Typography>
              </Stack>
            ) : null}
            <Divider />
            <Stack gap={1}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography fontSize={"22px"} fontWeight={600}>
                  {t(`${page}.total`)}
                </Typography>
                <Stack direction={"row"} spacing={1} alignItems={"center"}>
                  {defaultProductPrice === Number(discountedPrice) ? null : (
                    <Typography
                      fontSize={"22px"}
                      data-testid="total-price-ot"
                      sx={{ textDecoration: "line-through" }}
                      variant="subtitle1"
                    >
                      ${defaultProductPrice.toLocaleString()}
                    </Typography>
                  )}
                  <Typography
                    fontSize={"22px"}
                    fontWeight={600}
                    data-testid="total-price-ot"
                  >
                    ${discountedPrice.toLocaleString()}
                  </Typography>
                </Stack>
              </Stack>
              <Typography alignSelf="end">
                {t(`${page}.hsaDisclaimer`)}
              </Typography>
            </Stack>
            {initialDiscount === 0 ? null : (
              <Button
                variant="text"
                sx={{
                  backgroundColor: lighten(theme.palette.secondary.main, 0.3),
                  boxShadow: " 0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
                  color: "#ffffff",
                  fontSize: "16px",
                  fontWeight: 700,
                  borderRadius: "10px",
                  textAlign: "center",
                }}
                onClick={() => handleFocusIntoView("payment-input")}
              >
                {t(`${page}.totalSavings`, {
                  saving: totalSavings,
                })}
              </Button>
            )}
            <ReviewsCarousel onFocus={handleFocusIntoView} />
            <Divider sx={{ position: "relative", top: 15 }} />
          </Stack>
          {selectedProduct ? (
            <PaymentProcessor
              product={selectedProduct}
              coupon={coupon}
              dueToday={Number(discountedPrice)}
            />
          ) : null}
        </Stack>
      </Stack>
      <TermsOfConditions
        page={page}
        productSlug={productSlug}
        isGlp1={isGlp1}
        numberOfMonths={selectedProduct ? selectedProduct.number_of_months : 1}
      />
    </Stack>
  );
};

export default DiabetesPaymentInformation;
